import {useEffect, useRef, useState} from 'react';
import {GA4EventName} from '@Libraries/ga-events';
import useHasSeenAiCaptionIntroPopup from '@Hooks/auto-captions/useHasSeenAiCaptionIntroPopup';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import useAutoCaptionGlobal from '@Hooks/auto-captions/useAutoCaptionGlobal';
import {usePostId} from '@Components/social-media-wizard/social-media-wizard.hooks';

const WAIT_TIME_BEFORE_AI_CAPTION_BUTTON_PULSATES_SECS = 10000; // 10 seconds
const WAIT_TIME_BEFORE_AI_CAPTION_REMINDER_POPOVER_IS_VISIBLE = 20000; // 20 seconds

interface UseAiCaptionOnboardingOpts {
  accountId: number;
  accountIdsToPublishTo: number[];
  isUserEligibleToSeeTagsPopOver: boolean;
}

interface UseAiCaptionOnboardingReturn {
  isAiCaptionButtonPulsating: boolean;
  isVisibleAiCaptionReminderPopup: boolean;
  shouldShowIntroPopup: () => boolean;
  onAiCaptionClick: (viaOnboardingModal?: boolean) => void;
  afterCloseIntroPopup: () => void;
  onSeenReminderPopup: () => void;
  startPulsatingButtonTimer: () => void;
  abortPulsatingButtonTimer: () => void;
  setIsAiCaptionButtonPulsating: (value: boolean) => void;
}

export function useAiCaptionOnboarding({accountId, accountIdsToPublishTo, isUserEligibleToSeeTagsPopOver}: UseAiCaptionOnboardingOpts): UseAiCaptionOnboardingReturn {
  const [isAiCaptionButtonPulsating, setIsAiCaptionButtonPulsating] = useState<boolean>(false);
  const [isVisibleAiCaptionReminderPopup, setIsVisibleAiCaptionReminderPopup] = useState<boolean>(false);
  const aiCaptionsPulseTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const aiCaptionReminderPopupTimeout = useRef<NodeJS.Timeout>(undefined);

  const {hasSeenIntroPopup: userHasSeenAiCaptionPopup, setHasSeenIntroPopup, hasSeenReminderPopup, setHasSeenReminderPopup} = useHasSeenAiCaptionIntroPopup();
  const isMobile = useIsMobileWidth();
  const {setInAutoCaptionMode} = useAutoCaptionGlobal();
  const postId = usePostId();

  const shouldShowIntroPopup = (): boolean => {
    if (isUserEligibleToSeeTagsPopOver) {
      return false;
    }

    if (isMobile) {
      return false;
    }

    if (userHasSeenAiCaptionPopup) {
      return false;
    }

    return accountIdsToPublishTo[0] === accountId;
  };

  const shouldShowReminderPopup = (): boolean => {
    if (isUserEligibleToSeeTagsPopOver) {
      return false;
    }

    if (!userHasSeenAiCaptionPopup) {
      return false;
    }

    if (hasSeenReminderPopup) {
      return false;
    }

    return accountIdsToPublishTo[0] === accountId;
  };

  const startPulsatingButtonTimer = (): void => {
    if (!shouldShowReminderPopup()) {
      return;
    }

    aiCaptionsPulseTimeoutRef.current = setTimeout(() => {
      setIsAiCaptionButtonPulsating(true);
    }, WAIT_TIME_BEFORE_AI_CAPTION_BUTTON_PULSATES_SECS);

    if (!isMobile) {
      aiCaptionReminderPopupTimeout.current = setTimeout(() => {
        setIsVisibleAiCaptionReminderPopup(true);
        setHasSeenReminderPopup(true);
      }, WAIT_TIME_BEFORE_AI_CAPTION_REMINDER_POPOVER_IS_VISIBLE);
    }
  };

  const abortPulsatingButtonTimer = (): void => {
    if (aiCaptionsPulseTimeoutRef.current) {
      clearTimeout(aiCaptionsPulseTimeoutRef.current);
    }

    if (aiCaptionReminderPopupTimeout.current) {
      clearTimeout(aiCaptionReminderPopupTimeout.current);
    }

    setIsAiCaptionButtonPulsating(false);
    setIsVisibleAiCaptionReminderPopup(false);
  };

  const onSeenReminderPopup = (): void => {
    setHasSeenReminderPopup(true);
    setIsAiCaptionButtonPulsating(false);
    setIsVisibleAiCaptionReminderPopup(false);
  };

  const onAiCaptionClick = (viaOnboardingModal = false): void => {
    if (!postId) {
      return;
    }

    setInAutoCaptionMode({accountId});
    abortPulsatingButtonTimer();
    setIsAiCaptionButtonPulsating(false);

    if (!userHasSeenAiCaptionPopup) {
      setHasSeenIntroPopup(true);
    }

    if (userHasSeenAiCaptionPopup && !hasSeenReminderPopup && isVisibleAiCaptionReminderPopup) {
      onSeenReminderPopup();
    }

    if (viaOnboardingModal) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_ONBOARDING_TRYNOW);
    } else {
      if (isAiCaptionButtonPulsating) {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_REMINDER_VISIBLE_CLICK);
      }

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_CLICKED);
    }
  };

  const afterCloseIntroPopup = (): void => {
    setHasSeenIntroPopup(true);
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_ONBOARDING_LATER);
  };

  useEffect(() => {
    return (): void => {
      abortPulsatingButtonTimer();
    };
  }, []);

  return {
    isAiCaptionButtonPulsating,
    isVisibleAiCaptionReminderPopup,
    shouldShowIntroPopup,
    onAiCaptionClick,
    afterCloseIntroPopup,
    onSeenReminderPopup,
    startPulsatingButtonTimer,
    abortPulsatingButtonTimer,
    setIsAiCaptionButtonPulsating,
  };
}
